import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type SetStateAction
} from 'react';

import {
  accumulateSelections,
  calculatePriceMap,
  mapInputsToIds
} from '@paction-2.0/util';

import type { CalculationDto, CalculatorDto } from '@/lib/api/tools';
import { createCalculation, fetchCalculation } from '@/lib/api/tools';
import { trackEvent } from '@/lib/analytics';
import { logError } from '@/lib/logger';

import type { CostCalculatorBreed } from '../data/costCalculatorBreeds';

export type SelectionValue = string | string[];

export type SelectionMap = Record<string, SelectionValue>;

const DOG_COST_CALCULATION_TYPE = 'cost-calculator';

export const isNonEmptyString = (value: unknown): value is string =>
  typeof value === 'string' && value.trim().length > 0;

export type RawOption = Record<string, unknown> | null | undefined;

export type NormalizedOption = {
  value: string;
  title: string;
  description?: string;
};

export const normalizeOption = (
  option: RawOption,
  fallbackValue?: string | number | boolean
): NormalizedOption | null => {
  const record = option && typeof option === 'object' ? option : {};
  const rawValue =
    (record as Record<string, unknown>).value ??
    (record as Record<string, unknown>).id ??
    (record as Record<string, unknown>).legacyId ??
    (record as Record<string, unknown>).key ??
    (record as Record<string, unknown>).name ??
    fallbackValue;

  if (rawValue === undefined || rawValue === null) return null;
  const value = String(rawValue);

  const rawLabel =
    (record as Record<string, unknown>).label ??
    (record as Record<string, unknown>).name ??
    (record as Record<string, unknown>).title ??
    (record as Record<string, unknown>).text;
  const label = isNonEmptyString(rawLabel) ? rawLabel : value;

  const rawDescription =
    (record as Record<string, unknown>).description ??
    (record as Record<string, unknown>).subtitle ??
    (record as Record<string, unknown>).helper;
  const description = isNonEmptyString(rawDescription)
    ? rawDescription
    : undefined;

  return { value, title: label, description };
};

const LEGACY_DEFAULTS: Record<string, string> = {
  breed: 'affenpinscher',
  age: 'puppy',
  location: 'urban'
};

const LEGACY_DEFAULT_BREED_NAME = 'Affenpinscher';

const pickDefaultValue = (
  input: CalculatorDto['inputs'][number],
  preferred?: string
): NormalizedOption | null => {
  const normalizedPreferred = preferred?.toLowerCase();
  const match = (input.options ?? [])
    .map((option) => normalizeOption(option))
    .find((normalized) => {
      if (!normalized) return false;
      const value = normalized.value.toLowerCase();
      const title = normalized.title.toLowerCase();
      return value === normalizedPreferred || title === normalizedPreferred;
    });
  const fallback = normalizeOption(input.options?.[0], input.legacyId);
  return match ?? fallback;
};

const buildDefaultInputs = (calculator: CalculatorDto) => {
  const defaults: Record<string, string | number | boolean | null> = {};
  (calculator.inputs ?? []).forEach((input) => {
    const legacy = (input.legacyId || '').toLowerCase();
    const key = (input.name || input.label || '').toLowerCase();
    const preferred =
      legacy === 'breed'
        ? LEGACY_DEFAULTS.breed
        : legacy === 'age'
          ? LEGACY_DEFAULTS.age
          : legacy === 'location'
            ? LEGACY_DEFAULTS.location
            : key.includes('breed')
              ? LEGACY_DEFAULTS.breed
              : key.includes('age')
                ? LEGACY_DEFAULTS.age
                : key.includes('location')
                  ? LEGACY_DEFAULTS.location
                  : undefined;
    const option = pickDefaultValue(input, preferred);
    if (option) defaults[input.name] = option.value;
  });
  return defaults;
};

const buildInitialExpandedSections = (calculator: CalculatorDto) => {
  const map: Record<string, boolean> = {};
  (calculator.sections ?? []).forEach((section) => {
    map[section.legacyId] = true;
  });
  return map;
};

export const sectionAnchorFromLegacy = (
  sectionLegacyId?: string,
  sectionTitle?: string
) => {
  if (sectionLegacyId) return sectionLegacyId;
  if (!sectionTitle) return undefined;
  return sectionTitle
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, '')
    .trim()
    .replace(/\s+/g, '-');
};

export const useCostCalculatorState = (
  calculator: CalculatorDto,
  calculationId?: string,
  initialBreed?: string,
  breeds?: CostCalculatorBreed[]
) => {
  const resolvedBreeds = useMemo(() => breeds ?? [], [breeds]);

  const sizeInputName = useMemo(() => {
    const target = (calculator.inputs ?? []).find((input) => {
      const legacy = (input.legacyId || '').toLowerCase();
      if (legacy === 'size') return true;
      return (input.name || '').toLowerCase() === 'size';
    });
    return target?.name ?? 'size';
  }, [calculator.inputs]);

  const defaultInputs = useMemo(
    () => buildDefaultInputs(calculator),
    [calculator]
  );
  const defaultExpandedSections = useMemo(
    () => buildInitialExpandedSections(calculator),
    [calculator]
  );

  const [inputs, internalSetInputs] = useState<
    Record<string, string | number | boolean | null>
  >(() => ({
    ...defaultInputs,
    breed: LEGACY_DEFAULT_BREED_NAME,
    [sizeInputName]: null
  }));
  const [selections, setSelections] = useState<SelectionMap>({});
  const [results, setResults] = useState<Record<string, number | null>>({});
  const [isSaving, setIsSaving] = useState(false);
  const [loadedCalculation, setLoadedCalculation] =
    useState<CalculationDto | null>(null);
  const [expandedSections, setExpandedSections] = useState<
    Record<string, boolean>
  >(() => defaultExpandedSections);
  const [showCalculator, setShowCalculator] = useState(true);

  useEffect(() => {
    if (!calculationId) return;
    let active = true;
    fetchCalculation(calculationId)
      .then((calc) => {
        if (!active) return;
        setLoadedCalculation(calc);
        if (calc.inputs) internalSetInputs(calc.inputs);
        if (calc.responses) {
          const map: SelectionMap = {};
          calc.responses.forEach((resp) => {
            const record = resp as Record<string, unknown>;
            const qRaw = record.question;
            const q = isNonEmptyString(qRaw) ? qRaw : String(qRaw ?? '');

            const selectedDirect = record.selectedOption;
            const selectedOptions = record.selectedOptions;
            let selected: SelectionValue | undefined;

            if (Array.isArray(selectedOptions) && selectedOptions.length) {
              selected = selectedOptions.filter(isNonEmptyString);
            } else if (isNonEmptyString(selectedDirect)) {
              selected = selectedDirect;
            }

            if (q && selected) map[q] = selected;
          });
          setSelections(map);
        }
        if (calc.results)
          setResults(calc.results as Record<string, number | null>);
      })
      .catch((error) => {
        // Log error but don't block UI - calculator can still be used
        logError(
          '[useCostCalculatorState] Failed to load saved calculation',
          error
        );
      });
    return () => {
      active = false;
    };
  }, [calculationId]);

  const resolveBreed = useCallback(
    (token: string | null | undefined) => {
      if (!token) return null;
      const normalized = token.trim().toLowerCase();
      if (normalized.length === 0) return null;
      return (
        resolvedBreeds.find((b) => b.name.toLowerCase() === normalized) ??
        resolvedBreeds.find((b) => b.slug.toLowerCase() === normalized) ??
        null
      );
    },
    [resolvedBreeds]
  );

  const selectedSizeValue = inputs[sizeInputName];

  useEffect(() => {
    const raw = inputs.breed;
    if (typeof raw !== 'string') return;
    const match = resolveBreed(raw);
    if (!match?.size) return;
    const currentBreed = String(inputs.breed).toLowerCase();
    const matchBreed = match.name.toLowerCase();
    if (selectedSizeValue === match.size && currentBreed === matchBreed) {
      return;
    }
    internalSetInputs((prev) => ({
      ...prev,
      breed: match.name,
      [sizeInputName]: match.size
    }));
  }, [inputs.breed, resolveBreed, selectedSizeValue, sizeInputName]);

  useEffect(() => {
    if (!initialBreed) return;
    if (calculationId) return;

    // Don't override user selection: only apply the URL breed if we're still
    // on the legacy default (or empty).
    const current =
      typeof inputs.breed === 'string' ? inputs.breed.trim().toLowerCase() : '';
    if (current && current !== LEGACY_DEFAULT_BREED_NAME.toLowerCase()) {
      return;
    }

    const match = resolveBreed(initialBreed);
    if (!match) return;
    internalSetInputs((prev) => {
      const prevBreed =
        typeof prev.breed === 'string' ? prev.breed.trim().toLowerCase() : '';
      if (prevBreed && prevBreed !== LEGACY_DEFAULT_BREED_NAME.toLowerCase()) {
        return prev;
      }
      return {
        ...prev,
        breed: match.name,
        [sizeInputName]: match.size ?? prev[sizeInputName] ?? null
      };
    });
  }, [calculationId, initialBreed, inputs.breed, resolveBreed, sizeInputName]);

  useEffect(() => {
    setExpandedSections(defaultExpandedSections);
  }, [defaultExpandedSections]);

  useEffect(() => {
    if (loadedCalculation?.responses?.length) {
      setShowCalculator(true);
    }
  }, [loadedCalculation]);

  const mappedInputs = useMemo(
    () => mapInputsToIds(calculator.inputs ?? [], inputs),
    [calculator.inputs, inputs]
  );

  const priceMap = useMemo(
    () => calculatePriceMap(calculator.sections ?? [], mappedInputs),
    [calculator.sections, mappedInputs]
  );

  const questionLabelById = useMemo(() => {
    const map: Record<string, string> = {};
    (calculator.sections ?? []).forEach((section) => {
      (section.questions ?? []).forEach((question) => {
        if (!question?.legacyId) return;
        const label =
          typeof question.label === 'string' && question.label.trim().length > 0
            ? question.label.trim()
            : question.legacyId;
        map[question.legacyId] = label;
      });
    });
    return map;
  }, [calculator.sections]);

  const setInputs = useCallback(
    (
      updater:
        | Record<string, string | number | boolean | null>
        | SetStateAction<Record<string, string | number | boolean | null>>
    ) => {
      internalSetInputs(
        updater as SetStateAction<
          Record<string, string | number | boolean | null>
        >
      );
    },
    []
  );

  const onSelect = useCallback(
    (questionId: string, value: SelectionValue) => {
      setSelections((prev) => ({ ...prev, [questionId]: value }));

      const questionLabel = questionLabelById[questionId] ?? questionId;
      const renderedValue = Array.isArray(value)
        ? value.join(', ')
        : String(value);

      trackEvent({
        category: 'Question Option',
        action: 'Select',
        label: `${questionLabel}: ${renderedValue}`
      });
    },
    [questionLabelById]
  );

  const recomputeResults = useCallback(() => {
    const hasSelections = Object.keys(selections).length > 0;
    const nullTotals: Record<string, number | null> = {};

    calculator.outputs.forEach((out) => {
      nullTotals[out.legacyId] = hasSelections ? 0 : null;
    });

    if (!hasSelections) {
      setResults(nullTotals);
      return;
    }

    const accumulated = accumulateSelections(selections, priceMap);

    const totals: Record<string, number | null> = { ...nullTotals };
    Object.entries(accumulated).forEach(([key, value]) => {
      if (key in nullTotals) totals[key] = value;
    });

    setResults(totals);
  }, [calculator.outputs, priceMap, selections]);

  const resultsList = useMemo(
    () =>
      calculator.outputs.map((output) => ({
        id: output.legacyId,
        label: output.label ?? output.name,
        unit: output.unit ?? '',
        value: results[output.legacyId] ?? null
      })),
    [calculator.outputs, results]
  );

  const sectionTotals = useMemo(() => {
    const totals: Record<string, Record<string, number>> = {};
    calculator.sections.forEach((section) => {
      const sectionTotal: Record<string, number> = {};
      calculator.outputs.forEach((out) => {
        sectionTotal[out.legacyId] = 0;
      });

      section.questions.forEach((question) => {
        const selected = selections[question.legacyId];
        if (!selected) return;
        const values = Array.isArray(selected) ? selected : [selected];
        values.forEach((single) => {
          const outputSet = priceMap[question.legacyId]?.[single];
          if (!outputSet) return;
          Object.entries(outputSet).forEach(([outKey, outValue]) => {
            if (typeof outValue !== 'number') return;
            sectionTotal[outKey] = (sectionTotal[outKey] ?? 0) + outValue;
          });
        });
      });

      totals[section.legacyId] = sectionTotal;
    });
    return totals;
  }, [calculator.outputs, calculator.sections, priceMap, selections]);

  const defaultOutput = useMemo(
    () =>
      calculator.outputs.find((o) => (o as Record<string, unknown>).default) ??
      calculator.outputs.find(
        (o) => (o as Record<string, unknown>).isDefault
      ) ??
      calculator.outputs[0],
    [calculator.outputs]
  );

  const hasResults = useMemo(
    () => Object.values(results).some((v) => v !== null),
    [results]
  );

  const selectedBreed = useMemo(() => {
    const raw = inputs.breed;
    if (typeof raw !== 'string' || raw.trim().length === 0) return null;
    return resolveBreed(raw);
  }, [inputs.breed, resolveBreed]);

  const resultsTitle = useMemo(() => {
    if (!selectedBreed?.photoUrl) return 'Calculate the cost of owning a dog';
    const article = /^[aeiou]/i.test(selectedBreed.name) ? 'an' : 'a';
    return `Calculate the cost of owning ${article} ${selectedBreed.name}`;
  }, [selectedBreed?.name, selectedBreed?.photoUrl]);

  const handleCopyShare = useCallback(async () => {
    const shareUrl = 'https://paction.ca/dog-cost-calculator';

    try {
      await navigator.clipboard.writeText(shareUrl);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error(err);
    }
  }, []);

  const handleEmailSubmit = useCallback(
    async (email: string) => {
      setIsSaving(true);
      try {
        const payload = {
          calculator: calculator.legacyId,
          email,
          inputs,
          responses: Object.entries(selections).map(([question, selected]) => {
            const isArray = Array.isArray(selected);
            return {
              question,
              selectedOption: isArray ? null : selected,
              selectedOptions: isArray ? selected : null
            };
          }),
          results,
          type: DOG_COST_CALCULATION_TYPE
        };

        const savedCalculation = await createCalculation(payload);
        setLoadedCalculation(savedCalculation);
      } finally {
        setIsSaving(false);
      }
    },
    [calculator.legacyId, inputs, results, selections]
  );

  const handleClear = useCallback(() => {
    setSelections({});
    const legacyDefault =
      resolveBreed(LEGACY_DEFAULTS.breed) ??
      resolveBreed(LEGACY_DEFAULT_BREED_NAME);

    setInputs({
      ...defaultInputs,
      breed: legacyDefault?.name ?? LEGACY_DEFAULT_BREED_NAME,
      [sizeInputName]: legacyDefault?.size ?? null
    });
    setResults({});
    setExpandedSections(defaultExpandedSections);
    setShowCalculator(false);
  }, [
    defaultExpandedSections,
    defaultInputs,
    resolveBreed,
    setInputs,
    sizeInputName
  ]);

  return {
    inputs,
    setInputs,
    selections,
    onSelect,
    results,
    resultsList,
    sectionTotals,
    defaultOutput,
    hasResults,
    resultsTitle,
    selectedBreed,
    expandedSections,
    setExpandedSections,
    showCalculator,
    setShowCalculator,
    handleCopyShare,
    handleEmailSubmit,
    isSaving,
    handleClear,
    recomputeResults
  };
};

export default useCostCalculatorState;
